import {AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';

import {Store} from '@ngrx/store';

import {Observable, Subscription} from 'rxjs';

import {Label} from 'ng2-charts';

import {ChartDataSets, ChartOptions} from 'chart.js';

import {AppState} from '../../../../../state';
import {selectCompleted, selectUpload} from '../../../../state/upload/upload.selectors';
import {environment} from '../../../../../../environments/environment';
import {
  MODULE_ITEM_DEFAULT_BUTTON_LABEL,
  MODULE_ITEM_DEFAULT_IVY_COLOR,
  MODULE_ITEM_DEFAULT_NO_LABEL,
  MODULE_ITEM_DEFAULT_NO_OTHER_LABEL,
  MODULE_ITEM_DEFAULT_QUADRANT_IMAGE,
  MODULE_ITEM_DEFAULT_QUESTION,
  MODULE_ITEM_DEFAULT_YES_LABEL,
  SANITIZER_TYPES,
} from '../../../../../core/dashboard-constants';
import {StudyType} from '../../../../../models/study-type';
import {VideoUrl} from '../../../../../models/video-url';
import {ModuleItemAvailableSubType} from '../../../../../models/module-item-sub-type';
import {PreviewService} from '../../../../services/preview/preview.service';
import {prepareApiVideoUrl} from '../../../helpers/module-item-helper';
import {ModuleItemAvailableType} from '../../../../../models/module-item-type';
import {ModuleItem} from '../../../../../models/module-item';
import {UploadStatus} from '../../../../state/upload/upload.reducer';
import {Study} from "../../../../../models/study";

@Component({
  selector: 'dashboard-module-item-preview',
  templateUrl: './module-item-preview.component.html',
  styleUrls: ['./module-item-preview.component.scss'],
})
export class ModuleItemPreviewComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() studyType: string;
  @Input() type: string;
  @Input() subType: string;
  @Input() moduleItem: ModuleItem;
  @Input() study: Study;

  @ViewChild('topZone', { static: false }) topZone: ElementRef;
  @ViewChild('bottomZone', { static: false }) bottomZone: ElementRef;
  @ViewChild('dragWord', { static: false }) dragWord: ElementRef;
  @ViewChild('baseChart', {static: false}) baseChart: ElementRef;

  previewQuestion: string;
  previewDescription: string;
  previewImage: string | File;
  previewVideo: VideoUrl;
  previewButtonLabel: string;
  previewChoices: string[];
  previewMaxChars: number;
  previewAllowOther: boolean;
  previewYesLabel: string;
  previewNoLabel: string;
  previewNumberChoices: number[];
  previewMinValue: number;
  previewMaxValue: number;
  previewStepSize: number;
  previewLabelLeft: string;
  previewLabelRight: string;
  previewAudioVideo: string | File;
  previewQuadrantImage: string | File;
  fileUploadUuid: string;
  ivyElements = {} as any;
  YesNo = null;

  isVisibleOtherInput = false;

  uploadCompleted$: Observable<boolean>;

  publicUrl = environment.publicUrl;

  MODULE_ITEM_DEFAULT_QUADRANT_IMAGE = MODULE_ITEM_DEFAULT_QUADRANT_IMAGE;
  SANITIZER_TYPES = SANITIZER_TYPES;
  ModuleItemAvailableType = ModuleItemAvailableType;
  ModuleItemAvailableSubType = ModuleItemAvailableSubType;
  StudyType = StudyType;
  studyTitle: string;
  color: string;
  expand = false;
  selectedNumber: number;

  prepareApiVideoUrl = prepareApiVideoUrl;

  previewSelectedNumber: number;
  previewGraphLabels: Label[] = ['Value A', 'Value B', 'Value C', 'Value D'];
  previewGraphData: ChartDataSets[] = [];
  previewAxesOptions = {
    display: true,
    scaleLabel: {
      display: true,
      labelString: '',
      fontColor: '#000000',
      fontFamily: "'Maven', 'arial', 'sans-serif'",
      fontSize: 12,
      fontStyle: "normal",
      fontWeight: '400'
    },
    gridLines: {
      color: '#00000000',
      zeroLineColor: this.color,
      zeroLineWidth: 2,
    },
    ticks: {
      fontColor: '#000000',
      fontFamily: "'Maven', 'arial', 'sans-serif'",
      fontSize: 12,
      fontStyle: "normal",
      fontWeight: '600',
    }
  };
  previewGraphOptions: ChartOptions = {
    responsive: false,
    legend: { display: false },
    animation: { duration: 0 },
    scales: {
      xAxes: [this.previewAxesOptions],
      yAxes: [this.previewAxesOptions],
    },
  };

  private subscriptions = new Subscription();

  constructor(private store: Store<AppState>, private previewService: PreviewService) {}

  ngAfterViewChecked(): void {
    if (this.dragWord) {
      this.dragWord.nativeElement.style.backgroundColor = this.ivyElements.dragWordColor;
    }
  }

  ngOnInit(): void {
    const selectedStudyTranslations = this.study?.translations.filter(
      (translation) => translation.identifier === 'title'
    );
    this.studyTitle = selectedStudyTranslations[0].translation.toUpperCase();
    if (this.study.color) {
      this.changeColor(this.study.color);
    } else {
      this.changeColor('#009A8C');
    }

    this.previewAxesOptions.gridLines.zeroLineColor = this.color;

    this.previewGraphData = [{
      data: [6, 1, 12, 2],
      lineTension: 0,
      backgroundColor: this.color,
      borderColor: '#000000',
      pointBackgroundColor: this.color,
      pointBorderColor: this.color,
      borderWidth: 1,
      fill: false
    }];

    this.previewQuestion = MODULE_ITEM_DEFAULT_QUESTION;
    this.previewButtonLabel = MODULE_ITEM_DEFAULT_BUTTON_LABEL;
    this.previewYesLabel = MODULE_ITEM_DEFAULT_YES_LABEL;
    this.previewNoLabel =
      this.subType === ModuleItemAvailableSubType.YES_NO
        ? MODULE_ITEM_DEFAULT_NO_LABEL
        : MODULE_ITEM_DEFAULT_NO_OTHER_LABEL;
    this.previewQuadrantImage = MODULE_ITEM_DEFAULT_QUADRANT_IMAGE;

    this.subscriptions.add(
      this.previewService.previewData$.subscribe((previewData) => {
        if (previewData?.question) {
          this.previewQuestion = previewData?.question;
        }
        if (previewData?.description) {
          this.previewDescription = previewData?.description;
        }
        if (previewData?.buttonLabel) {
          this.previewButtonLabel = previewData?.buttonLabel;
        }
        if (previewData?.imageUrl) {
          this.previewImage = previewData?.imageUrl;
        }
        if (previewData?.videoUrl) {
          this.previewVideo = previewData?.videoUrl;
        }
        if (previewData?.choices?.length) {
          this.previewChoices = previewData?.choices;
        }
        if (previewData?.maxNumberOfChars) {
          this.previewMaxChars = previewData?.maxNumberOfChars;
        }
        if (typeof previewData?.allowOther === 'boolean') {
          this.previewAllowOther = previewData?.allowOther;
        }
        if (previewData?.yesLabel) {
          this.previewYesLabel = previewData?.yesLabel;
        }
        if (previewData?.noLabel) {
          this.previewNoLabel = previewData?.noLabel;
        }
        if (previewData?.numberChoices?.length) {
          this.previewNumberChoices = previewData?.numberChoices;
        }
        if (previewData?.minValue) {
          this.previewMinValue = previewData?.minValue;
        }
        if (previewData?.maxValue) {
          this.previewMaxValue = previewData?.maxValue;
        }
        if (previewData?.stepSize) {
          this.previewStepSize = previewData?.stepSize;
        }
        if (previewData?.labelLeft) {
          this.previewLabelLeft = previewData?.labelLeft;
        }
        if (previewData?.labelRight) {
          this.previewLabelRight = previewData?.labelRight;
        }
        if (previewData?.audioVideo) {
          this.previewAudioVideo = this.publicUrl + previewData?.audioVideo;
        }
        if (previewData?.xAxis) {
          this.populateAxisLabel('xAxes', previewData?.xAxis);
        }
        if (previewData?.yAxis) {
          this.populateAxisLabel('yAxes', previewData?.yAxis);
        }
        if (previewData?.quadrantImageUrl) {
          this.previewQuadrantImage = previewData?.quadrantImageUrl;
        }
        if (previewData?.fileUploadUuid) {
          this.fileUploadUuid = previewData?.fileUploadUuid;
          this.selectUploadedFiles();
        }
        if (previewData.ivyElements) {
          if (typeof previewData.ivyElements?.hideInfo === 'boolean') {
            this.ivyElements.hideInfo = previewData.ivyElements.hideInfo;
          }
          if (typeof previewData.ivyElements?.audioVideo === 'string' || previewData.ivyElements?.audioVideo === null) {
            this.ivyElements[`${previewData.ivyElements.zone}ZoneAudio`] = previewData.ivyElements.audioVideo;
          }

          this.ivyElements.selectedLanguageCode = previewData.ivyElements.selectedLanguage?.code;
          this.ivyElements.translations = {};
          previewData.ivyElements?.translations?.forEach((translationControl) => {
            this.ivyElements.translations[translationControl.language] =
              this.ivyElements.translations[translationControl.language] || {};
            this.ivyElements.translations[translationControl.language][translationControl.identifier] =
              translationControl.translation;
          });

          this.ivyElements.topZoneColor = previewData.ivyElements.topZoneColor || MODULE_ITEM_DEFAULT_IVY_COLOR;
          this.ivyElements.bottomZoneColor = previewData.ivyElements.bottomZoneColor || MODULE_ITEM_DEFAULT_IVY_COLOR;
          this.ivyElements.dragWordColor = previewData.ivyElements.dragWordColor || MODULE_ITEM_DEFAULT_IVY_COLOR;
        }
      })
    );
  }

  populateAxisLabel(axisName, axisLabel): void {
    this.previewGraphOptions = {
      ...this.previewGraphOptions,
      scales: {
        ...this.previewGraphOptions.scales,
        [axisName]: [
          {
            ...this.previewAxesOptions,
            scaleLabel: { ...this.previewAxesOptions.scaleLabel, labelString: axisLabel },
          },
        ],
      },
    };
  }

  selectUploadedFiles(): void {
    this.uploadCompleted$ = this.store.select(selectCompleted(this.fileUploadUuid));

    this.subscriptions.add(
      this.store.select(selectUpload(this.fileUploadUuid)).subscribe((upload) => {
        if (upload?.status === UploadStatus.RESET) {
          this.previewAudioVideo = '';
          this.previewQuadrantImage = MODULE_ITEM_DEFAULT_QUADRANT_IMAGE;
          this.previewImage = '';
        }
        if (!upload?.uploaded?.length) {
          return;
        }
        if (
          upload?.uploaded[0]?.toString().startsWith('videos') ||
          upload?.uploaded[0]?.toString().startsWith('audios')
        ) {
          this.previewAudioVideo = this.publicUrl + upload?.uploaded[0];
        } else {
          this.previewImage = upload?.uploaded[0];
        }
      })
    );
  }

  changeOtherInputVisibility(isVisible: boolean): void {
    this.isVisibleOtherInput = isVisible;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onIvyZoneClick(event) {
    const elId = event.target.id || event.target.parentElement.id;
    this[elId].nativeElement.style.backgroundColor = this.ivyElements[`${elId}Color`];

    if (this.ivyElements[`${elId}Audio`]) {
      const audio = new Audio(environment.publicUrl + this.ivyElements[`${elId}Audio`]);
      audio.load();
      audio.onended = () => (this[elId].nativeElement.style.backgroundColor = MODULE_ITEM_DEFAULT_IVY_COLOR);
      audio.onerror = audio.onended;
      audio.play().catch(() => audio.onended(null));
    } else {
      setTimeout(() => {
        this[elId].nativeElement.style.backgroundColor = MODULE_ITEM_DEFAULT_IVY_COLOR;
      }, 1000);
    }
  }
  changeColor(color: string) {
    if (color) {
      this.color = color;
    }
  }

  openNumberDropdown() {
    this.expand = !this.expand;
  }

  selectOption(option: number) {
    this.selectedNumber = option;
    this.expand = !this.expand;
  }

  setYesNo(bool: boolean)
  {
    this.YesNo = bool;
  }
}
